import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, resolveImg } from "../lib/api";
import { Nav } from "../components/Nav";
import { Users, Send, Megaphone, DollarSign, Loader2, Sparkles, Upload, Trash2, MapPin, Gift, Search, ImagePlus } from "lucide-react";

const TIERS = ["city", "state", "nationwide"];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [cats, setCats] = useState([]);
  const [tab, setTab] = useState("overview");

  const [gen, setGen] = useState({ category: "Birthday", title: "", prompt: "" });
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState("");

  // Bulk "Add photos" tool
  const [bulkCat, setBulkCat] = useState("");
  const [bulkQuery, setBulkQuery] = useState("");
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkSearching, setBulkSearching] = useState(false);
  const [bulkUrls, setBulkUrls] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");

  const searchStock = async () => {
    if (!bulkQuery.trim()) { setBulkMsg("Enter a keyword to search."); return; }
    setBulkSearching(true); setBulkMsg(""); setBulkResults([]); setBulkSelected({});
    try {
      const { data } = await api.get("/admin/stock/search", { params: { query: bulkQuery, count: 12 } });
      setBulkResults(data.results || []);
      if ((data.results || []).length === 0) setBulkMsg("No photos found. Try another keyword.");
    } catch (e) {
      setBulkMsg(e?.response?.data?.detail || "Search unavailable. Paste image URLs below instead.");
    } finally { setBulkSearching(false); }
  };

  const toggleSelect = (url) => setBulkSelected((s) => ({ ...s, [url]: !s[url] }));

  const addBulk = async (items) => {
    if (!bulkCat.trim()) { setBulkMsg("Enter a category name first."); return; }
    if (items.length === 0) { setBulkMsg("Select at least one photo (or paste URLs)."); return; }
    setBulkSaving(true); setBulkMsg("");
    try {
      const { data } = await api.post("/admin/templates/bulk", { category: bulkCat.trim(), items });
      setBulkMsg(`✅ Added ${data.added} template${data.added === 1 ? "" : "s"} to "${data.category}"!`);
      setBulkSelected({}); setBulkResults([]); setBulkUrls(""); setBulkQuery("");
      api.get("/templates").then(({ data }) => setTemplates(data));
      api.get("/templates/categories").then(({ data }) => setCats(data.filter((c) => c !== "All")));
    } catch (e) {
      setBulkMsg(e?.response?.data?.detail || "❌ Could not add templates. Try again.");
    } finally { setBulkSaving(false); }
  };

  const addSelectedPhotos = () => {
    const items = bulkResults.filter((r) => bulkSelected[r.url]).map((r) => ({ image_url: r.url, title: null }));
    addBulk(items);
  };

  const addPastedUrls = () => {
    const items = bulkUrls.split("\n").map((u) => u.trim()).filter(Boolean).map((u) => ({ image_url: u, title: null }));
    addBulk(items);
  };

  const [autoFilling, setAutoFilling] = useState(false);
  const [fillCount, setFillCount] = useState(6);
  const runAutofill = async (params, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setAutoFilling(true); setBulkMsg("");
    try {
      const { data } = await api.post("/admin/templates/autofill", null, { params });
      setBulkMsg(`✅ ${data.message}`);
      api.get("/templates").then(({ data }) => setTemplates(data));
      api.get("/templates/categories").then(({ data }) => setCats(data.filter((c) => c !== "All")));
    } catch (e) {
      setBulkMsg(e?.response?.data?.detail || "❌ Auto-fill failed. Try again.");
    } finally { setAutoFilling(false); }
  };
  const fillThisCategory = () => {
    if (!bulkCat.trim()) { setBulkMsg("Type a category name above first, then fill it."); return; }
    runAutofill({ category: bulkCat.trim(), per_category: fillCount }, `Add ${fillCount} realistic photos to "${bulkCat.trim()}"?`);
  };
  const topUpAll = () => runAutofill({ min_count: fillCount, per_category: fillCount }, `Top up every category to at least ${fillCount} templates?`);

  const adFileRef = useRef();
  const [ad, setAd] = useState({ business_name: "", headline: "", cta_text: "Learn More", link_url: "", tier: "nationwide", target_city: "", target_state: "", days: 365 });
  const [adImage, setAdImage] = useState(null);
  const [adUploading, setAdUploading] = useState(false);
  const [adSaving, setAdSaving] = useState(false);
  const [adMsg, setAdMsg] = useState("");

  const setAdField = (k) => (e) => setAd({ ...ad, [k]: e.target.value });

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/users").then(({ data }) => setUsers(data));
    api.get("/admin/campaigns").then(({ data }) => setCampaigns(data));
    api.get("/admin/reports").then(({ data }) => setReports(data));
    api.get("/templates").then(({ data }) => setTemplates(data));
    api.get("/templates/categories").then(({ data }) => setCats(data.filter((c) => c !== "All")));
  };
  useEffect(load, []);

  const resolveReport = async (id) => {
    await api.post(`/admin/reports/${id}/resolve`);
    setReports((rs) => rs.map((r) => (r.id === id ? { ...r, status: "resolved" } : r)));
  };

  const uploadAdImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAdUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAdImage(data.url);
    } catch { setAdMsg("❌ Image upload failed."); } finally { setAdUploading(false); }
  };

  const createHouseAd = async () => {
    if (!ad.business_name || !ad.headline || !ad.link_url) { setAdMsg("Add a business name, headline and link."); return; }
    if (ad.tier === "city" && !ad.target_city) { setAdMsg("Enter a target city."); return; }
    if (ad.tier === "state" && !ad.target_state) { setAdMsg("Enter a target state."); return; }
    setAdSaving(true); setAdMsg("");
    try {
      await api.post("/admin/campaigns", { ...ad, days: Number(ad.days) || 365, image_url: adImage });
      setAdMsg("✅ Ad added to the rotation — no charge!");
      setAd({ business_name: "", headline: "", cta_text: "Learn More", link_url: "", tier: "nationwide", target_city: "", target_state: "", days: 365 });
      setAdImage(null);
      api.get("/admin/campaigns").then(({ data }) => setCampaigns(data));
    } catch { setAdMsg("❌ Could not create the ad. Try again."); } finally { setAdSaving(false); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this ad/campaign permanently?")) return;
    try {
      await api.delete(`/admin/campaigns/${id}`);
      setCampaigns((cs) => cs.filter((c) => c.id !== id));
    } catch {
      alert("Could not delete this campaign. Please try again.");
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template permanently?")) return;
    try {
      await api.delete(`/admin/templates/${id}`);
      setTemplates((ts) => ts.filter((t) => t.id !== id));
    } catch {
      alert("Could not delete this template. Please try again.");
    }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete ${name || "this user"} and all their greetings permanently?`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers((us) => us.filter((u) => u.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not delete this user. Please try again.");
    }
  };

  const generate = async () => {
    if (!gen.title || !gen.prompt) { setGenMsg("Add a title and prompt."); return; }
    setGenerating(true); setGenMsg("");
    try {
      await api.post("/admin/templates/generate", gen);
      setGenMsg("✅ Template generated and added to the gallery!");
      setGen({ ...gen, title: "", prompt: "" });
    } catch {
      setGenMsg("❌ Generation failed. Try again.");
    } finally { setGenerating(false); }
  };

  if (!stats) return <div className="min-h-screen"><Nav /><div className="grid place-items-center py-32"><Loader2 className="w-8 h-8 animate-spin" /></div></div>;

  const cards = [
    { icon: Users, label: "Total users", value: stats.total_users, bg: "#A8E6CF" },
    { icon: Send, label: "Greetings sent", value: stats.total_greetings, bg: "#F9F871" },
    { icon: Megaphone, label: "Total campaigns", value: stats.total_campaigns, bg: "#FFB5A7" },
    { icon: DollarSign, label: "Revenue", value: `$${stats.revenue.toLocaleString()}`, bg: "#C8B6FF" },
  ];

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-7xl mx-auto px-5 py-10">
        <h1 className="text-3xl sm:text-4xl font-display font-bold">Admin panel</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-6" data-testid="admin-stats">
          {cards.map((c) => (
            <div key={c.label} className="nb-card p-6" style={{ background: c.bg }}>
              <c.icon className="w-7 h-7" />
              <div className="text-3xl font-display font-bold mt-3">{c.value}</div>
              <div className="text-xs font-bold uppercase tracking-widest mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-10">
          {["overview", "users", "campaigns", "free ad", "reports", "templates", "add photos", "generate"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.replace(" ", "-")}`}
              className={`px-4 py-2 rounded-xl font-bold border-2 border-[#0A0A0A] transition-all capitalize ${tab === t ? "bg-[#FFB5A7] shadow-[3px_3px_0px_#0A0A0A]" : "bg-white hover:bg-[#F9F871]"}`}>
              {t}{t === "reports" && reports.filter((r) => r.status === "open").length > 0 ? ` (${reports.filter((r) => r.status === "open").length})` : ""}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="nb-card p-7 mt-6">
            <h2 className="text-xl font-display font-bold mb-3">Revenue by tier</h2>
            {Object.keys(stats.revenue_by_tier).length === 0 ? <p className="text-[#4A4A4A]">No paid campaigns yet.</p> : (
              <div className="space-y-2">
                {Object.entries(stats.revenue_by_tier).map(([tier, count]) => (
                  <div key={tier} className="flex items-center justify-between border-b border-gray-200 py-2">
                    <span className="font-semibold capitalize">{tier}</span><span className="font-bold">{count} campaigns</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-sm text-[#4A4A4A] mt-4">Total campaigns: {stats.total_campaigns}</p>
          </div>
        )}

        {tab === "users" && (
          <div className="nb-card p-0 mt-6 overflow-x-auto" data-testid="admin-users-table">
            <table className="w-full text-left">
              <thead className="bg-[#F9F871] border-b-2 border-[#0A0A0A]">
                <tr>{["Name", "Email", "Role", "Location", ""].map((h) => <th key={h} className="px-5 py-3 text-xs font-bold uppercase tracking-widest">{h}</th>)}</tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-200">
                    <td className="px-5 py-3 font-semibold">{u.name}</td>
                    <td className="px-5 py-3">{u.email}</td>
                    <td className="px-5 py-3"><span className="text-xs font-bold uppercase px-2 py-1 rounded-lg border-2 border-[#0A0A0A]">{u.role}</span></td>
                    <td className="px-5 py-3 text-[#4A4A4A]">{[u.city, u.state].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-5 py-3">
                      {u.role !== "admin" && (
                        <button onClick={() => deleteUser(u.id, u.name)} data-testid={`delete-user-${u.id}`}
                          title="Delete user"
                          className="w-8 h-8 grid place-items-center rounded-lg border-2 border-[#0A0A0A] bg-white hover:bg-[#FFD5CC] transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "campaigns" && (() => {
          const totalImpr = campaigns.reduce((s, c) => s + (c.impressions || 0), 0);
          const totalClicks = campaigns.reduce((s, c) => s + (c.clicks || 0), 0);
          const avgCtr = totalImpr > 0 ? ((totalClicks / totalImpr) * 100) : 0;
          const perf = [
            { label: "Total impressions", value: totalImpr.toLocaleString(), bg: "#A8E6CF" },
            { label: "Total clicks", value: totalClicks.toLocaleString(), bg: "#F9F871" },
            { label: "Avg. click-through rate", value: `${avgCtr.toFixed(1)}%`, bg: "#C8B6FF" },
          ];
          return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-6" data-testid="admin-ad-performance">
              {perf.map((p) => (
                <div key={p.label} className="nb-card p-5" style={{ background: p.bg }}>
                  <div className="text-3xl font-display font-bold">{p.value}</div>
                  <div className="text-xs font-bold uppercase tracking-widest mt-1">{p.label}</div>
                </div>
              ))}
            </div>
            <div className="nb-card p-0 mt-6 overflow-x-auto" data-testid="admin-campaigns-table">
              <table className="w-full text-left">
                <thead className="bg-[#F9F871] border-b-2 border-[#0A0A0A]">
                  <tr>{["Business", "Tier", "Target", "Status", "Impr.", "Clicks", "CTR", ""].map((h) => <th key={h} className="px-5 py-3 text-xs font-bold uppercase tracking-widest">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => {
                    const ctr = c.impressions > 0 ? (c.clicks / c.impressions) * 100 : 0;
                    return (
                    <tr key={c.id} className="border-b border-gray-200">
                      <td className="px-5 py-3 font-semibold">
                        {c.business_name}
                        {c.house && <span className="ml-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-md border-2 border-[#0A0A0A] bg-[#C8B6FF]">House</span>}
                      </td>
                      <td className="px-5 py-3 capitalize">{c.tier}</td>
                      <td className="px-5 py-3">{c.target_city || c.target_state || "US"}</td>
                      <td className="px-5 py-3">{c.status.replace("_", " ")}</td>
                      <td className="px-5 py-3">{c.impressions}</td>
                      <td className="px-5 py-3">{c.clicks}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2 min-w-[110px]">
                          <span className="font-bold tabular-nums w-12">{ctr.toFixed(1)}%</span>
                          <div className="flex-1 h-2.5 rounded-full bg-gray-200 border border-[#0A0A0A] overflow-hidden">
                            <div className="h-full bg-[#FFB5A7]" style={{ width: `${Math.min(ctr, 100)}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <button onClick={() => deleteCampaign(c.id)} data-testid={`delete-campaign-${c.id}`}
                          className="w-8 h-8 grid place-items-center rounded-lg border-2 border-[#0A0A0A] bg-white hover:bg-[#FFD5CC] transition-colors" aria-label="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                  {campaigns.length === 0 && <tr><td colSpan={8} className="px-5 py-6 text-center text-[#4A4A4A]">No campaigns yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
          );
        })()}

        {tab === "free ad" && (
          <div className="nb-card p-7 mt-6 max-w-xl" data-testid="admin-free-ad">
            <h2 className="text-xl font-display font-bold flex items-center gap-2"><Gift className="w-5 h-5" /> Place a free ad</h2>
            <p className="text-[#4A4A4A] text-sm mt-1">Add your own ad straight into the rotation — no payment required. Great for filling empty slots.</p>
            <div className="space-y-4 mt-5">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest">Business / title</span>
                <input value={ad.business_name} onChange={setAdField("business_name")} data-testid="ad-business"
                  placeholder="SendOccasions" className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest">Headline</span>
                <input value={ad.headline} onChange={setAdField("headline")} data-testid="ad-headline"
                  placeholder="Send unlimited free greetings!" className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5" />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-widest">Button text</span>
                  <input value={ad.cta_text} onChange={setAdField("cta_text")} data-testid="ad-cta"
                    className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5" />
                </label>
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-widest">Run for (days)</span>
                  <input type="number" min="1" value={ad.days} onChange={setAdField("days")} data-testid="ad-days"
                    className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5" />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest">Link URL</span>
                <input value={ad.link_url} onChange={setAdField("link_url")} data-testid="ad-link"
                  placeholder="https://yourlink.com" className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest">Targeting</span>
                <select value={ad.tier} onChange={setAdField("tier")} data-testid="ad-tier"
                  className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5 capitalize">
                  {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              {ad.tier === "city" && (
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-widest flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Target city</span>
                  <input value={ad.target_city} onChange={setAdField("target_city")} data-testid="ad-city"
                    placeholder="Austin" className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5" />
                </label>
              )}
              {ad.tier === "state" && (
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-widest flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Target state</span>
                  <input value={ad.target_state} onChange={setAdField("target_state")} data-testid="ad-state"
                    placeholder="Texas" className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5" />
                </label>
              )}
              <div>
                <span className="text-xs font-bold uppercase tracking-widest">Ad image (optional)</span>
                <input ref={adFileRef} type="file" accept="image/*" onChange={uploadAdImage} className="hidden" data-testid="ad-image-input" />
                <button type="button" onClick={() => adFileRef.current?.click()} data-testid="ad-image-btn"
                  className="nb-btn bg-white px-5 py-2.5 mt-1.5 flex items-center gap-2">
                  {adUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {adImage ? "Change image" : "Upload image"}
                </button>
                {adImage && <img src={resolveImg(adImage)} alt="ad preview" className="mt-3 w-full max-w-xs rounded-xl border-2 border-[#0A0A0A]" />}
              </div>
              <button onClick={createHouseAd} disabled={adSaving} data-testid="ad-submit" className="nb-btn bg-[#A8E6CF] w-full py-3.5">
                {adSaving ? <><Loader2 className="w-5 h-5 animate-spin" /> Adding…</> : "Add to rotation (free)"}
              </button>
              {adMsg && <p className="font-semibold text-sm" data-testid="ad-message">{adMsg}</p>}
            </div>
          </div>
        )}

        {tab === "reports" && (
          <div className="nb-card p-0 mt-6 overflow-x-auto" data-testid="admin-reports-table">
            <table className="w-full text-left">
              <thead className="bg-[#F9F871] border-b-2 border-[#0A0A0A]">
                <tr>{["Reported message", "Sender", "Reason", "Status", ""].map((h) => <th key={h} className="px-5 py-3 text-xs font-bold uppercase tracking-widest">{h}</th>)}</tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} className="border-b border-gray-200 align-top">
                    <td className="px-5 py-3 max-w-xs"><span className="line-clamp-2">{r.message || "—"}</span>
                      <Link to={`/g/${r.share_token}`} className="text-xs underline text-[#4A4A4A]">view card</Link>
                    </td>
                    <td className="px-5 py-3 font-semibold">{r.sender_name || "—"}</td>
                    <td className="px-5 py-3 text-[#4A4A4A]">{r.reason || "—"}</td>
                    <td className="px-5 py-3"><span className={`text-xs font-bold uppercase px-2 py-1 rounded-lg border-2 border-[#0A0A0A] ${r.status === "open" ? "bg-[#FFD5CC]" : "bg-[#A8E6CF]"}`}>{r.status}</span></td>
                    <td className="px-5 py-3">{r.status === "open" && <button onClick={() => resolveReport(r.id)} data-testid={`resolve-${r.id}`} className="nb-btn bg-white px-3 py-1.5 text-xs">Resolve</button>}</td>
                  </tr>
                ))}
                {reports.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-center text-[#4A4A4A]">No reports — all clear! 🎉</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "templates" && (
          <div className="mt-6" data-testid="admin-templates">
            <p className="text-sm text-[#4A4A4A] mb-4">{templates.length} templates. Delete removes a single template permanently.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {templates.map((t) => (
                <div key={t.id} className="nb-card p-0 overflow-hidden" data-testid={`admin-template-${t.id}`}>
                  <div className="h-28 border-b-2 border-[#0A0A0A] relative">
                    {t.image_url ? (
                      <img src={resolveImg(t.image_url)} alt={t.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full" style={{ background: t.gradient || "#FFB5A7" }} />
                    )}
                    <button onClick={() => deleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}
                      title="Delete template"
                      className="absolute top-1.5 right-1.5 w-8 h-8 grid place-items-center rounded-lg border-2 border-[#0A0A0A] bg-white/95 hover:bg-[#FFD5CC]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-2">
                    <div className="font-bold text-sm truncate">{t.title}</div>
                    <div className="text-xs text-[#4A4A4A] truncate">{t.category}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "add photos" && (
          <div className="nb-card p-7 mt-6 max-w-3xl" data-testid="admin-bulk">
            <h2 className="text-xl font-display font-bold flex items-center gap-2"><ImagePlus className="w-5 h-5" /> Bulk add templates</h2>
            <p className="text-[#4A4A4A] text-sm mt-1">Add lots of realistic photo templates at once. Type any category (new ones like "Fourth of July" are created automatically).</p>

            <div className="mt-4 p-4 rounded-xl border-2 border-[#0A0A0A] bg-[#F9F871]">
              <div className="font-display font-bold flex items-center gap-2"><Sparkles className="w-4 h-4" /> Quick auto-fill with realistic photos</div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <span className="text-sm font-semibold"># of photos:</span>
                <input type="number" min={1} max={24} value={fillCount} data-testid="fill-count"
                  onChange={(e) => setFillCount(Math.max(1, Math.min(24, Number(e.target.value) || 1)))}
                  className="w-20 nb-border rounded-lg px-3 py-2 bg-white" />
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={fillThisCategory} disabled={autoFilling} data-testid="fill-this-category" className="nb-btn bg-white px-4 py-2.5 flex items-center gap-2">
                  {autoFilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                  Fill "{bulkCat.trim() || "this category"}" with {fillCount}
                </button>
                <button onClick={topUpAll} disabled={autoFilling} data-testid="bulk-autofill" className="nb-btn bg-white px-4 py-2.5 flex items-center gap-2">
                  {autoFilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Top up all thin categories to {fillCount}
                </button>
              </div>
              <div className="text-xs text-[#0A0A0A]/70 mt-2">Type a category in the box below to fill just that one, or top up every category that has fewer than your chosen number.</div>
            </div>

            <label className="block mt-5">
              <span className="text-xs font-bold uppercase tracking-widest">Category</span>
              <input value={bulkCat} onChange={(e) => setBulkCat(e.target.value)} data-testid="bulk-category" list="bulk-cats"
                placeholder="e.g. Fourth of July" className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5" />
              <datalist id="bulk-cats">{cats.map((c) => <option key={c} value={c} />)}</datalist>
            </label>

            <div className="mt-6">
              <span className="text-xs font-bold uppercase tracking-widest">1 · Search realistic photos</span>
              <div className="flex gap-2 mt-1.5">
                <input value={bulkQuery} onChange={(e) => setBulkQuery(e.target.value)} data-testid="bulk-search-input"
                  onKeyDown={(e) => e.key === "Enter" && searchStock()}
                  placeholder="e.g. fireworks american flag" className="flex-1 nb-border rounded-xl px-4 py-3 bg-white" />
                <button onClick={searchStock} disabled={bulkSearching} data-testid="bulk-search-btn" className="nb-btn bg-[#8AC6FF] px-5 py-3 flex items-center gap-2">
                  {bulkSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
                </button>
              </div>

              {bulkResults.length > 0 && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4" data-testid="bulk-results">
                    {bulkResults.map((r) => (
                      <button key={r.url} onClick={() => toggleSelect(r.url)} data-testid={`bulk-photo-${r.id}`}
                        className={`relative rounded-xl overflow-hidden border-2 h-28 transition-all ${bulkSelected[r.url] ? "border-[#0A0A0A] ring-4 ring-[#A8E6CF]" : "border-[#0A0A0A] opacity-90 hover:opacity-100"}`}>
                        <img src={r.thumb} alt={r.alt} className="w-full h-full object-cover" />
                        {bulkSelected[r.url] && <span className="absolute inset-0 bg-[#0A0A0A]/20 grid place-items-center"><span className="bg-[#A8E6CF] border-2 border-[#0A0A0A] rounded-full w-7 h-7 grid place-items-center font-bold">✓</span></span>}
                      </button>
                    ))}
                  </div>
                  <button onClick={addSelectedPhotos} disabled={bulkSaving} data-testid="bulk-add-selected" className="nb-btn bg-[#A8E6CF] px-5 py-3 mt-4">
                    {bulkSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</> : `Add ${Object.values(bulkSelected).filter(Boolean).length} selected to "${bulkCat || "…"}"`}
                  </button>
                </>
              )}
            </div>

            <div className="mt-8 pt-6 border-t-2 border-dashed border-[#0A0A0A]">
              <span className="text-xs font-bold uppercase tracking-widest">2 · Or paste image URLs (one per line)</span>
              <textarea value={bulkUrls} onChange={(e) => setBulkUrls(e.target.value)} rows={4} data-testid="bulk-urls"
                placeholder={"https://images.unsplash.com/photo-...\nhttps://images.pexels.com/photos/..."} className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5 resize-none font-mono text-sm" />
              <button onClick={addPastedUrls} disabled={bulkSaving} data-testid="bulk-add-urls" className="nb-btn bg-[#FFB5A7] px-5 py-3 mt-3">
                {bulkSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</> : "Add pasted URLs"}
              </button>
            </div>

            {bulkMsg && <p className="font-semibold text-sm mt-4" data-testid="bulk-message">{bulkMsg}</p>}
          </div>
        )}

        {tab === "generate" && (
          <div className="nb-card p-7 mt-6 max-w-xl" data-testid="admin-generate">
            <h2 className="text-xl font-display font-bold flex items-center gap-2"><Sparkles className="w-5 h-5" /> Generate AI template</h2>
            <p className="text-[#4A4A4A] text-sm mt-1">Create a new greeting card background with AI (Nano Banana).</p>
            <div className="space-y-4 mt-5">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest">Category</span>
                <input value={gen.category} onChange={(e) => setGen({ ...gen, category: e.target.value })} data-testid="gen-category"
                  list="gen-cats" placeholder="Type a category (e.g. Fourth of July)"
                  className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5" />
                <datalist id="gen-cats">{cats.map((c) => <option key={c} value={c} />)}</datalist>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest">Title</span>
                <input value={gen.title} onChange={(e) => setGen({ ...gen, title: e.target.value })} data-testid="gen-title"
                  placeholder="Sunny Birthday Wishes" className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest">Prompt</span>
                <textarea value={gen.prompt} onChange={(e) => setGen({ ...gen, prompt: e.target.value })} rows={3} data-testid="gen-prompt"
                  placeholder="Colorful balloons and confetti, pastel tones" className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5 resize-none" />
              </label>
              <button onClick={generate} disabled={generating} data-testid="gen-submit" className="nb-btn bg-[#FFB5A7] w-full py-3.5">
                {generating ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating…</> : "Generate template"}
              </button>
              {genMsg && <p className="font-semibold text-sm" data-testid="gen-message">{genMsg}</p>}
            </div>
            <button
              className="nb-btn mt-4"
              data-testid="fix-template-images-btn"
              onClick={() => api.post("/admin/templates/reset-photos").then(() => alert("Photos fixed!"))}
            >
              Fix Scrambled Template Images
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
